/**
 * SoldStage — JOB-SOLD-01, the job's Sold tab (Seth 2026-09-28: "I want this process to feel clean and easy.
 * Where everyone knows where to find everything and nothing gets missed … the 'sold' tab should feel like it
 * has what the tech originally sold and the pictures they uploaded and everything that would be needed to see
 * the scope and do the load calc. Then we verify and enter the proper equipment based on the load calc").
 *
 * The office works it left to right, top to bottom:
 *   1 · the sale and the tech's packet — What Was Sold (frozen, off the signed proposal), From the Tech (the
 *       photo checklist, the site facts, notes, utility accounts, preferred day), the Installer Guide so far
 *   2 · the Load Calc — drop the report and the numbers are read off it, or type them, or "not needed" + why
 *   3 · the Equipment Check — sold vs the load vs on the job, a plain verdict per system, swap a unit to
 *       another size of the same line, then Confirm the Equipment (who / when / why). That sign-off is what
 *       finishes the Sold stage; change a unit afterwards and it asks again.
 */
import { useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, BadgeCheck, Calculator, Camera, Check, CheckCircle2, ChevronDown, ChevronUp, ClipboardList, Truck,
  Download, ExternalLink, Flame, HandCoins, Loader2, Map as MapIcon, MessageSquareText, Package, Pencil, Plus, RefreshCw,
  Snowflake, Sparkles, Trash2, Undo2, Upload, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { toastError } from "@/components/ui/mutationToast";
import { useMemberName } from "@/components/TeamMemberSelect";
import {
  isImageFile, useJobFiles, useSignedUrls, useUploadFiles, useSortFiles, type PegasusFile,
} from "@/features/files/filesCore";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";
import { downloadZip, zipTitle } from "@/features/files/zip";
import { useQuote, lineTotal, optionTotal, lineWarrantyLabel, type QuoteLine, type QuoteOptionFull, type Quote } from "@/features/quotes/quotes";
import { useQuoteCategories } from "@/features/quotes/quotesAdmin";
import { SalePaymentLine } from "@/features/quotes/SalePaymentLine";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { itemCostOf } from "@/features/items/pricebookSearch";
import type { Item } from "@/features/items/items";
import {
  useAddJobEquipment, useCreateFinalizeVisit, useDeleteJobEquipment, useJobVisits, useUpdateJobEquipment,
  type Job, type JobEquipment, type JobQuote,
} from "./jobs";
import { useJobAhri } from "./ahri";
import { specGroups, specLines, specProgress, useInstallBank, useJobEquipFacts } from "./installSpec";
import {
  btuh, capacityChips, capacityOf, fmtTons, matchNotes, seriesPrefix, sizingCheck,
  type Capacity, type SizingLine, type UnitText,
} from "./capacity";
import {
  effectiveLoad, hasPacket, loadRead, majorUnits, salePhotos, siteCaption, soldState, usePersonName,
  useConfirmEquipment, useItemSpecs, useLoadCalcRules, useSalePhotoSlots, useSaveLoadCalc, useSeriesItems, useSiteFacts,
  type EffectiveLoad, type ItemSpec, type LoadCalcRules, type SalePacket, type SoldState,
} from "./saleHandoff";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtWhen = (s: string | null | undefined) =>
  !s ? "" : new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtDay = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? `${s}T12:00:00` : s).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const MAJOR_KINDS = new Set(["furnace", "ac", "heat_pump", "water_heater", "boiler", "mini_split", "air_handler", "coil"]);

/** "2400" + sq ft → "2,400 sq ft" (a year stays a year). */
function factText(v: string, f: { key: string; unit?: string }): string {
  const plain = /^\d[\d,]*(\.\d+)?$/.test(v);
  const n = Number(v.replace(/,/g, ""));
  const yearish = f.key === "year_built" || (!f.unit && n >= 1800 && n <= 2100);
  const shown = plain && Number.isFinite(n) && n >= 1000 && !yearish ? n.toLocaleString("en-US") : v;
  return `${shown}${f.unit && plain ? ` ${f.unit}` : ""}`;
}

/* ── Frame ───────────────────────────────────────────────────────────────── */

function Card({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return <section id={id} className={cn("scroll-mt-28 rounded-xl border border-hairline bg-white", className)}>{children}</section>;
}
function CardHead({ icon: Icon, title, info, meta, right }: {
  icon: typeof Package; title: string; info?: ReactNode; meta?: ReactNode; right?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 px-5 py-3.5">
      <h3 className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
        <Icon className="h-5 w-5 text-brand-500" /> {title}
        {info && <InfoTip label={`About ${title}`}>{info}</InfoTip>}
      </h3>
      {meta && <div className="min-w-0 text-[14px] text-slate-500">{meta}</div>}
      {right && <div className="ml-auto flex flex-wrap items-center gap-2">{right}</div>}
    </header>
  );
}
const linkBtn = "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50";

/** One unit's words for the capacity reader: the job row plus its pricebook item. */
function unitText(u: { category?: string | null; model?: string | null; part_number?: string | null; equip_key?: string | null; kind?: string | null; item_id?: string | null; description?: string | null },
  specs: Map<string, ItemSpec> | undefined): UnitText {
  const it = u.item_id ? specs?.get(u.item_id) : undefined;
  return {
    category: u.category ?? it?.category ?? it?.equip_type ?? null,
    equip_key: u.equip_key ?? null,
    kind: u.kind ?? null,
    model: u.model || it?.model || null,
    part_number: u.part_number || it?.part_number || null,
    description: [u.description, it?.description].filter(Boolean).join(" · ") || null,
    efficiency: it?.efficiency ?? null,
  };
}
const unitName = (u: { manufacturer?: string | null; model?: string | null; part_number?: string | null; category?: string | null }) => {
  const model = (u.model || u.part_number || "").trim();
  const maker = (u.manufacturer ?? "").trim();
  // "NEST" + "NEST THERMOSTAT" reads once.
  const both = maker && model && !model.toUpperCase().startsWith(maker.toUpperCase()) ? `${maker} ${model}` : model || maker;
  return both || u.category || "Unit";
};

function RoleIcon({ role, className }: { role: string; className?: string }) {
  if (role === "furnace" || role === "boiler") return <Flame className={cn("text-orange-500", className)} />;
  if (role === "ac" || role === "heat_pump") return <Snowflake className={cn("text-sky-500", className)} />;
  return <Package className={cn("text-slate-400", className)} />;
}
function Chips({ items, tone = "slate" }: { items: string[]; tone?: "slate" | "brand" }) {
  if (!items.length) return null;
  return (
    <span className="flex flex-wrap gap-1.5">
      {items.map((c) => (
        <span key={c} className={cn("rounded-full px-2.5 py-0.5 text-[13px] font-semibold tabular-nums",
          tone === "brand" ? "bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100" : "bg-slate-100 text-slate-700")}>{c}</span>
      ))}
    </span>
  );
}

/* ── The stage ───────────────────────────────────────────────────────────── */

export function SoldStage({ job, quote, units, closed, onGo }: {
  job: Job;
  quote: JobQuote | null;
  units: JobEquipment[];
  closed: boolean;
  onGo: (stage: "workorder" | "scheduled" | "closed", zone?: string) => void;
}) {
  const { data: files } = useJobFiles(job.id);
  const { rules } = useLoadCalcRules();
  const load = effectiveLoad(job, files);
  const st = soldState(job, units, load, rules);
  const { data: full } = useQuote(quote?.id ?? null);
  const accepted = full?.options.find((o) => o.id === full.quote.accepted_option_id) ?? null;
  const specs = useItemSpecs([...units.map((u) => u.item_id), ...(accepted?.lines.map((l) => l.item_id) ?? [])]).data;
  const { data: slotsQ } = useSalePhotoSlots();
  const { data: factsQ } = useSiteFacts();
  const slots = (slotsQ ?? []).filter((s) => s?.label?.trim());
  const factList = (factsQ ?? []).filter((f) => f?.label?.trim());
  const packet = (job.sale_packet ?? null) as SalePacket | null;
  const photos = salePhotos(files);
  const reqSlots = slots.filter((s) => s.required);
  const reqPhotosMissing = reqSlots.filter((s) => !photos.slotted.some((f) => f.caption === siteCaption(s.label.trim()))).length;
  const reqFacts = factList.filter((f) => f.required);
  const reqFactsMissing = reqFacts.filter((f) => !(packet?.facts?.[f.key] ?? "").trim()).length;
  const scroll = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="space-y-5">
      <HandoffStrip
        steps={[
          {
            id: "zone-sold-tech", n: 1, title: "The Sale & the Tech's Packet",
            done: reqPhotosMissing === 0 && reqFactsMissing === 0 && (hasPacket(packet) || photos.slotted.length > 0),
            status: !hasPacket(packet) && photos.slotted.length === 0 && photos.other.length === 0 ? "Nothing from the phone yet"
              : reqPhotosMissing + reqFactsMissing === 0 ? `${photos.slotted.length + photos.other.length} photos · facts in`
              : [reqPhotosMissing ? `${reqPhotosMissing} photo${reqPhotosMissing === 1 ? "" : "s"} missing` : null,
                 reqFactsMissing ? `${reqFactsMissing} fact${reqFactsMissing === 1 ? "" : "s"} missing` : null].filter(Boolean).join(" · "),
            warn: reqPhotosMissing + reqFactsMissing > 0,
          },
          {
            id: "zone-load-calc", n: 2, title: "Load Calc",
            done: load.source !== "none",
            status: load.source === "not_needed" ? "Not needed"
              : load.source === "none" ? (load.reading ? "Reading the report…" : rules.required ? "Needed" : "Optional")
              : [load.heat ? `Heat ${Math.round(load.heat / 1000)}K` : null, load.cool ? `Cool ${Math.round(load.cool / 1000)}K` : null].filter(Boolean).join(" · ") || "On file",
          },
          {
            id: "zone-equip-check", n: 3, title: "Equipment Check",
            done: st.done,
            status: st.done ? `Confirmed ${fmtDay(job.equip_confirmed_at)}` : st.stale ? "Changed — confirm again" : majorUnits(units).length === 0 ? "Add the equipment" : "Not confirmed",
            warn: st.stale,
          },
        ]}
        onPick={scroll}
      />

      {/* SALE-FINALIZE-01: nothing from the house yet (the homeowner said yes
          after the visit) → send the selling tech back on a Finalize visit. */}
      {!closed && !hasPacket(packet) && photos.slotted.length === 0 && photos.other.length === 0 && (
        <FinalizeNudge job={job} />
      )}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)]">
        <div className="space-y-5">
          <WhatWasSold job={job} quote={quote} accepted={accepted} options={full?.options ?? []} fullQuote={full?.quote ?? null} specs={specs} onPayments={() => onGo("closed", "zone-payments")} />
          <FromTheTech job={job} packet={packet} photos={photos} slots={slots} facts={factList} />
          <GuideSummary job={job} units={units} onOpen={() => onGo("workorder", "zone-install-details")} />
        </div>
        <div className="space-y-5">
          <LoadCalcCard job={job} load={load} rules={rules} closed={closed} />
          <EquipmentCheck job={job} units={units} accepted={accepted} specs={specs} load={load} rules={rules} st={st} closed={closed}
            onDetails={() => onGo("workorder", "zone-equipment")} />
        </div>
      </div>
    </div>
  );
}

/* ── SALE-FINALIZE-01: the empty-packet nudge ────────────────────────────── */

function FinalizeNudge({ job }: { job: Job }) {
  const memberName = useMemberName();
  const { data: visits } = useJobVisits(job.id);
  const create = useCreateFinalizeVisit();
  const survey = (visits ?? []).find((v) => v.visit_role === "survey" && v.status !== "cancelled" && v.status !== "complete");
  const tech = job.sale_tech ? memberName(job.sale_tech) ?? job.sale_tech : null;
  if (survey) {
    const when = survey.scheduled_date
      ? new Date(survey.scheduled_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
      : null;
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50/70 px-4 py-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-100 text-sky-700"><Truck className="h-4.5 w-4.5" /></span>
        <div className="min-w-0 flex-1 text-sm text-slate-700">
          <b className="font-semibold text-slate-900">Finalize visit is on the board</b>
          {" — "}{when ? `${tech ?? "the tech"} goes back ${when}${survey.scheduled_window ? ` (${survey.scheduled_window})` : ""}.` : "waiting to be scheduled from the Board's Finalize sales lane."}
          {" "}The photos, facts and installer guide land here when they send the packet.
        </div>
        <Link to="/board" className={linkBtn}>Board <ArrowRight className="h-4 w-4" /></Link>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700"><Camera className="h-4.5 w-4.5" /></span>
      <div className="min-w-0 flex-1 text-sm text-slate-700">
        <b className="font-semibold text-slate-900">Nothing From the House Yet.</b>{" "}
        The homeowner said yes after the visit, so there are no site photos, measurements or installer notes to run the load calc from.
        Send {tech ?? "the selling tech"} back — on the phone that stop opens the Install Packet.
      </div>
      <Button size="sm" disabled={create.isPending}
        onClick={() => create.mutate({ job }, { onError: (e) => toastError(`Couldn't add the visit — ${(e as Error).message || "try again"}`) })}>
        <Truck className="h-4 w-4" /> {create.isPending ? "Adding…" : `Send ${tech ? tech.split(" ")[0] : "the Tech"} Back to Finalize`}
      </Button>
    </div>
  );
}

/* ── The three-step strip ────────────────────────────────────────────────── */

function HandoffStrip({ steps, onPick }: {
  steps: { id: string; n: number; title: string; status: string; done: boolean; warn?: boolean }[];
  onPick: (id: string) => void;
}) {
  const current = steps.find((s) => !s.done)?.n ?? null;
  return (
    <ol className="grid gap-2 sm:grid-cols-3">
      {steps.map((s) => (
        <li key={s.n}>
          <button type="button" onClick={() => onPick(s.id)}
            className={cn("flex h-full w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all",
              current === s.n ? "bg-white shadow-e2 ring-2 ring-inset ring-brand-600" : "bg-white/70 ring-1 ring-inset ring-hairline hover:bg-white hover:ring-slate-300")}>
            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold",
              s.done ? "bg-emerald-600 text-white" : current === s.n ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500")}>
              {s.done ? <Check className="h-4 w-4" strokeWidth={3} /> : s.n}
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-bold leading-tight text-slate-900">{s.title}</span>
              <span className={cn("mt-0.5 block truncate text-[13px] font-medium", s.warn ? "text-amber-700" : s.done ? "text-emerald-700" : "text-slate-500")}>{s.status}</span>
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/* ── 1 · What was sold ───────────────────────────────────────────────────── */

function WhatWasSold({ job, quote, accepted, options, fullQuote, specs, onPayments }: {
  job: Job; quote: JobQuote | null; accepted: QuoteOptionFull | null; options: QuoteOptionFull[]; fullQuote: Quote | null; specs: Map<string, ItemSpec> | undefined;
  onPayments: () => void;
}) {
  const memberName = useMemberName();
  const { data: cats } = useQuoteCategories(true);
  const equipKinds = useMemo(() => new Set([...(cats ?? []).filter((c) => c.kind === "equipment").map((c) => c.key), ...MAJOR_KINDS]), [cats]);
  const others = options.filter((o) => o.id !== accepted?.id);
  const lines = accepted?.lines ?? [];
  const major = lines.filter((l) => equipKinds.has(l.kind));
  // QUOTE-POLISH-10 (Seth: "we want to see what the tech offered, what packages,
  // what recommended settings"): the package as sold, by how each line rode.
  const chosenIds = new Set<string>(fullQuote?.selected_addons ?? []);
  const inPackage = lines.filter((l) => !equipKinds.has(l.kind) && (l.role === "included" || l.role === "required"));
  // QUOTE-POLISH-13: Optional lines ride in the package; "Their Call" shows which they kept and which they took off.
  const theirCall = lines.filter((l) => !equipKinds.has(l.kind) && l.role === "optional");
  const offeredAddons = lines.filter((l) => l.role === "recommended");
  const rest = [...inPackage, ...theirCall.filter((l) => chosenIds.has(l.id)), ...offeredAddons.filter((l) => chosenIds.has(l.id))];
  const soldBy = job.sale_tech ? memberName(job.sale_tech) ?? job.sale_tech : null;
  const sorted = [...options].sort((a, b) => a.sort - b.sort);
  const isFree = (l: QuoteLine) => lineTotal(l) === 0 && l.qty * l.unit_price > 0;
  return (
    <Card id="zone-sold-tech">
      <CardHead icon={HandCoins} title="What Was Offered & Sold"
        meta={accepted
          ? <span>{accepted.label || accepted.tier}{accepted.brand ? ` · ${accepted.brand}` : ""}{quote?.accepted_at ? ` · signed ${fmtDay(quote.accepted_at)}` : ""}{soldBy ? ` · ${soldBy}` : ""}</span>
          : soldBy ? <span>Sold by {soldBy}</span> : null}
        right={quote ? <Link to={`/quotes/${quote.id}`} className={linkBtn}>View Proposal <ArrowRight className="h-4 w-4" /></Link> : null}
      />
      {!accepted ? (
        <div className="px-5 py-4 text-[15px] text-slate-600">
          {quote ? "The proposal on this job hasn't marked which option they took." : "No proposal is linked to this job."}
          {" "}Contract <b className="font-semibold text-slate-900">{money(job.sell_price)}</b>.
        </div>
      ) : (
        <>
          {/* What was OFFERED — every collection the tech put in front of them, the
              one the office recommended, and the one they took. */}
          {sorted.length > 1 && (
            <div className="grid gap-2 border-b border-slate-100 px-5 py-3 sm:grid-cols-3">
              {sorted.map((o) => {
                const took = o.id === accepted?.id;
                const rec = fullQuote?.recommended_option_id === o.id;
                const oEquip = o.lines.filter((l) => equipKinds.has(l.kind));
                return (
                  <div key={o.id} className={cn("rounded-xl border px-3 py-2", took ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-white")}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={cn("text-[13px] font-bold", took ? "text-emerald-800" : "text-slate-700")}>{o.label || o.tier}{o.brand ? ` · ${o.brand}` : ""}</span>
                      <span className="text-[13px] font-bold tabular-nums text-slate-800">{money(optionTotal(o))}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {took && <span className="rounded bg-emerald-600 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-white">Chosen</span>}
                      {rec && <span className="rounded bg-amber-200 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-900">★ Recommended</span>}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-[12px] text-slate-500">
                      {oEquip.map((l) => <li key={l.id} className="truncate">{[l.manufacturer, l.model].filter(Boolean).join(" ") || l.description}</li>)}
                      {oEquip.length === 0 && <li className="italic text-slate-400">No equipment</li>}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {major.map((l) => <SoldLineRow key={l.id} l={l} specs={specs} warranty={accepted ? lineWarrantyLabel(l, accepted) : undefined} />)}
          </ul>
          {(inPackage.length > 0 || theirCall.length > 0 || offeredAddons.length > 0) && (
            <div className="grid gap-x-6 gap-y-3 border-t border-slate-100 px-5 py-3 sm:grid-cols-2">
              {inPackage.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-400">In the Package</div>
                  <ul className="space-y-1">
                    {inPackage.map((l) => (
                      <li key={l.id} className="flex items-baseline justify-between gap-3 text-[14px]">
                        <span className="min-w-0 truncate text-slate-700">{l.qty > 1 ? `${l.qty}× ` : ""}{l.description || l.model || "Item"}</span>
                        <span className="shrink-0 text-[12px] font-semibold tabular-nums">
                          {isFree(l) ? <span className="text-emerald-700">Included · <s className="text-slate-400">{money(l.qty * l.unit_price)}</s></span> : <span className="text-slate-600">{money(lineTotal(l))}{l.role === "required" ? " · locked" : ""}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(theirCall.length > 0 || offeredAddons.length > 0) && (
                <div>
                  <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-400">Their Call</div>
                  <ul className="space-y-1">
                    {theirCall.map((l) => {
                      const took = chosenIds.has(l.id);
                      return (
                        <li key={l.id} className="flex items-baseline justify-between gap-3 text-[14px]">
                          <span className={cn("min-w-0 truncate", took ? "text-slate-800" : "text-slate-400 line-through decoration-slate-300")}>{l.qty > 1 ? `${l.qty}× ` : ""}{l.description || l.model || "Item"}</span>
                          <span className={cn("shrink-0 text-[12px] font-semibold tabular-nums", took ? "text-emerald-700" : "text-slate-400")}>{took ? `✓ kept · ${isFree(l) ? "Included" : money(lineTotal(l))}` : `took it off · ${isFree(l) ? "Included" : money(lineTotal(l))}`}</span>
                        </li>
                      );
                    })}
                    {offeredAddons.map((l) => {
                      const took = chosenIds.has(l.id);
                      return (
                        <li key={l.id} className="flex items-baseline justify-between gap-3 text-[14px]">
                          <span className={cn("min-w-0 truncate", took ? "text-slate-800" : "text-slate-400")}>★ {l.description || l.model || "Add-On"}</span>
                          <span className={cn("shrink-0 text-[12px] font-semibold tabular-nums", took ? "text-emerald-700" : "text-slate-400")}>{took ? `✓ added · ${money(lineTotal(l))}` : `offered · ${money(lineTotal(l))}`}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
          {rest.length === 0 && major.length === 0 && (
            <div className="px-5 py-3 text-[14px] text-slate-500">No line items on the accepted collection.</div>
          )}
          <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-[14px]">
            <span className="text-slate-500">Contract <b className="text-[17px] font-bold tabular-nums text-slate-900">{money(job.sell_price ?? optionTotal(accepted))}</b></span>
            {quote && <SalePaymentLine quote={quote} />}
            <button type="button" onClick={onPayments} className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline">
              Payments <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {others.length > 0 && (
              <span className="ml-auto text-slate-500">
                Also offered: {others.map((o) => `${o.label || o.tier} ${money(optionTotal(o))}`).join(" · ")}
              </span>
            )}
          </footer>
        </>
      )}
    </Card>
  );
}

function SoldLineRow({ l, specs, warranty }: { l: QuoteLine; specs: Map<string, ItemSpec> | undefined; warranty?: string }) {
  const it = l.item_id ? specs?.get(l.item_id) : undefined;
  const text = unitText({ category: it?.category ?? it?.equip_type ?? l.kind.replace(/_/g, " "), model: l.model, description: l.description, item_id: l.item_id }, specs);
  const cap = capacityOf(text);
  const role = cap.role !== "other" ? cap.role : l.kind;
  return (
    <li className="flex items-start gap-3 px-5 py-3">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-100">
        <RoleIcon role={role} className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-[15px] font-semibold text-slate-900">{l.qty > 1 ? `${l.qty}× ` : ""}{[l.manufacturer, l.model].filter(Boolean).join(" ") || l.description || "Unit"}</span>
          <span className="text-[15px] font-semibold tabular-nums text-slate-700">{money(lineTotal(l))}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Chips items={capacityChips(cap)} />
          {/* QUOTE-POLISH-08: the warranty this unit was sold with. */}
          {warranty && <span className="rounded bg-emerald-50 px-1.5 py-px text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">{warranty}</span>}
        </div>
      </div>
    </li>
  );
}

/* ── 1 · From the tech ───────────────────────────────────────────────────── */

function FromTheTech({ job, packet, photos, slots, facts }: {
  job: Job;
  packet: SalePacket | null;
  photos: { slotted: PegasusFile[]; other: PegasusFile[] };
  slots: { label: string; required: boolean; hint?: string }[];
  facts: { key: string; label: string; unit?: string; required?: boolean }[];
}) {
  const personOf = usePersonName();
  const [open, setOpen] = useState<PegasusFile | null>(null);
  const [zipping, setZipping] = useState<string | null>(null);
  const { data: visits } = useJobVisits(job.id);
  const bySlot = new Map<string, PegasusFile[]>();
  for (const f of photos.slotted) {
    const label = (f.caption ?? "").replace(/^Site — /, "");
    bySlot.set(label, [...(bySlot.get(label) ?? []), f]);
  }
  // Slots named in Settings first, then any photo whose slot was renamed or removed since.
  const known = new Set(slots.map((s) => s.label.trim()));
  const extraSlots = [...bySlot.keys()].filter((k) => !known.has(k));
  const ordered = [
    ...slots.map((s) => ({ label: s.label.trim(), required: s.required, hint: s.hint, files: bySlot.get(s.label.trim()) ?? [] })),
    ...extraSlots.map((k) => ({ label: k, required: false, hint: undefined, files: bySlot.get(k) ?? [] })),
  ];
  const all = [...ordered.flatMap((s) => s.files), ...photos.other];
  const urls = useSignedUrls(all.filter((f) => isImageFile(f))).data;
  const legacy = !hasPacket(packet) ? (visits ?? []).map((v) => v.notes ?? "").find((n) => /INSTALL PACKET|INSTALL PREP/.test(n)) : undefined;
  const u = packet?.utility ?? {};
  const util = [
    [u.eu, u.ea ? `#${u.ea}` : null].filter(Boolean).join(" "),
    [u.gu, u.ga ? `#${u.ga}` : null].filter(Boolean).join(" "),
  ].filter(Boolean);
  const knownFacts = new Set(facts.map((f) => f.key));
  const strayFacts = Object.entries(packet?.facts ?? {}).filter(([k, v]) => !knownFacts.has(k) && (v ?? "").trim());
  const addr = job.address ? `${job.address}${/\bMN\b/.test(job.address) ? "" : ", MN"}` : null;

  const zipAll = async () => {
    setZipping("0");
    try {
      await downloadZip(all, `${zipTitle(job.address ?? job.customer_name)} — sale photos`,
        (f) => {
          const ext = (/\.[A-Za-z0-9]{1,6}$/.exec(f.filename)?.[0] ?? ".jpg").toLowerCase();
          const label = /^Site — /.test(f.caption ?? "") ? (f.caption ?? "").replace(/^Site — /, "") : f.filename.replace(/\.[^.]+$/, "");
          return `${label}${ext}`;
        },
        (d, t) => setZipping(`${d} of ${t}`));
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Couldn't make the zip.");
    } finally {
      setZipping(null);
    }
  };

  return (
    <Card>
      <CardHead icon={Camera} title="From the Tech"
        info="The photo checklist and the site facts come from Settings → Sold Jobs & Load Calc. The tech fills them in the install packet on the phone; the office does the load calc from them."
        meta={packet?.sent_at
          ? <span>Sent {fmtWhen(packet.sent_at)}{packet.tech ? ` by ${packet.tech}` : packet.sent_by ? ` by ${personOf(packet.sent_by)}` : ""}</span>
          : hasPacket(packet) ? <span className="font-medium text-amber-700">Saved on the phone — not sent yet</span>
          : photos.slotted.length + photos.other.length > 0 ? <span>Photos only</span>
          : <span>Nothing from the phone yet</span>}
        right={<>
          {addr && (
            <a href={`https://www.google.com/maps?q=${encodeURIComponent(addr)}&t=k`} target="_blank" rel="noreferrer" className={linkBtn}
              title="The house from above — handy for the outside walls">
              <MapIcon className="h-4 w-4" /> Satellite View
            </a>
          )}
          {all.length > 0 && (
            <button type="button" onClick={() => void zipAll()} disabled={!!zipping} className={cn(linkBtn, "disabled:opacity-60")}>
              {zipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {zipping ? `Zipping ${zipping}…` : `Download ${all.length} Photo${all.length === 1 ? "" : "s"}`}
            </button>
          )}
        </>}
      />

      <div className="space-y-5 px-5 py-4">
        {ordered.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {ordered.map((s) => {
              const first = s.files[0];
              const url = first && isImageFile(first) ? urls?.get(first.storage_path) : undefined;
              return first ? (
                <button key={s.label} type="button" onClick={() => setOpen(first)} title={first.ai_summary ?? s.label}
                  className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-100 ring-1 ring-inset ring-hairline transition hover:ring-2 hover:ring-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  {url ? <img src={url} alt={s.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    : <span className="flex h-full w-full items-center justify-center text-slate-400"><Camera className="h-6 w-6" /></span>}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent px-2.5 pb-2 pt-6 text-left text-[13px] font-semibold leading-tight text-white">
                    {s.label}
                  </span>
                  {s.files.length > 1 && (
                    <span className="absolute right-2 top-2 rounded-full bg-slate-950/70 px-2 py-0.5 text-[12px] font-bold tabular-nums text-white">{s.files.length}</span>
                  )}
                </button>
              ) : (
                <div key={s.label} title={s.hint}
                  className={cn("flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-2 text-center",
                    s.required ? "border-amber-300 bg-amber-50/60 text-amber-800" : "border-slate-200 bg-slate-50/60 text-slate-400")}>
                  <Camera className="h-5 w-5" />
                  <span className="text-[13px] font-semibold leading-tight">{s.label}</span>
                  <span className="text-[12px] font-medium">{s.required ? "Missing" : "Not taken"}</span>
                </div>
              );
            })}
          </div>
        )}

        {photos.other.length > 0 && (
          <div>
            <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-400">More From the Sale · {photos.other.length}</div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-2">
              {photos.other.map((f) => (
                <button key={f.id} type="button" onClick={() => setOpen(f)} title={f.ai_summary ?? f.caption ?? f.filename}
                  className="aspect-square overflow-hidden rounded-lg bg-slate-100 ring-1 ring-inset ring-hairline transition hover:ring-2 hover:ring-brand-400">
                  {urls?.get(f.storage_path) ? <img src={urls.get(f.storage_path)} alt={f.ai_summary ?? f.filename} loading="lazy" className="h-full w-full object-cover" />
                    : <span className="flex h-full w-full items-center justify-center text-slate-400"><Camera className="h-5 w-5" /></span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {(facts.length > 0 || strayFacts.length > 0) && (
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {facts.map((f) => {
              const v = (packet?.facts?.[f.key] ?? "").trim();
              return (
                <div key={f.key} className="min-w-0">
                  <dt className="text-[13px] font-medium text-slate-500">{f.label}</dt>
                  <dd className={cn("mt-0.5 text-[15px] font-semibold", v ? "text-slate-900" : f.required ? "text-amber-700" : "text-slate-300")}>
                    {v ? factText(v, f) : f.required ? "Not given" : "—"}
                  </dd>
                </div>
              );
            })}
            {strayFacts.map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="text-[13px] font-medium text-slate-500">{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</dt>
                <dd className="mt-0.5 text-[15px] font-semibold text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>
        )}

        {(packet?.notes ?? "").trim() && (
          <div className="flex gap-2.5 rounded-xl bg-slate-50 px-4 py-3">
            <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-800">{packet!.notes}</p>
          </div>
        )}

        {(util.length > 0 || packet?.pref_day) && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-[14px]">
            {util.length > 0 && <span className="text-slate-500">Utility accounts <b className="font-semibold text-slate-800">{util.join(" · ")}</b></span>}
            {packet?.pref_day && <span className="text-slate-500">Would like the install <b className="font-semibold text-slate-800">{fmtDay(packet.pref_day)}</b></span>}
          </div>
        )}

        {legacy && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
            <div className="mb-1 text-[12px] font-bold uppercase tracking-wide text-slate-400">From the Phone</div>
            <pre className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed text-slate-700">{legacy}</pre>
          </div>
        )}
      </div>
      <FilePreviewModal file={open} custno={job.custno} siblings={all} onClose={() => setOpen(null)} />
    </Card>
  );
}

/* ── 1 · The installer guide so far ──────────────────────────────────────── */

function GuideSummary({ job, units, onOpen }: { job: Job; units: JobEquipment[]; onOpen: () => void }) {
  const facts = useJobEquipFacts(units);
  const bank = useInstallBank();
  const prog = specProgress(job.install_spec, facts, bank);
  const groups = specGroups(specLines(job.install_spec, facts, bank), bank);
  const [all, setAll] = useState(false);
  const shown = all ? groups : groups.slice(0, 3);
  const open = prog.total - prog.answered;
  return (
    <Card>
      <CardHead icon={ClipboardList} title="Installer Guide"
        meta={facts.lines === 0 ? null : prog.total === 0 ? null : prog.complete
          ? <span className="font-semibold text-emerald-700">All {prog.total} answered</span>
          : <span>{prog.answered} of {prog.total} answered{open ? ` · ${open} open` : ""}</span>}
        right={<button type="button" onClick={onOpen} className={linkBtn}>{prog.answered ? "Open in Work Order" : "Fill It In"} <ArrowRight className="h-4 w-4" /></button>}
      />
      <div className="px-5 py-4">
        {facts.lines === 0 ? (
          <p className="text-[15px] text-slate-500">The questions follow the equipment — once it&apos;s on the job, the tech answers them on the phone or the office does in Work Order.</p>
        ) : groups.length === 0 ? (
          <p className="text-[15px] text-slate-500">Nothing answered yet.</p>
        ) : (
          <div className="space-y-4">
            {shown.map((g) => (
              <div key={g.title}>
                <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-400">{g.title}</div>
                <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                  {g.lines.map((l) => (
                    <div key={l.key} className="flex min-w-0 items-baseline gap-2 text-[14px]">
                      <dt className="shrink-0 text-slate-500">{l.label}</dt>
                      <dd className="min-w-0 font-semibold text-slate-800">{l.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
            {groups.length > 3 && (
              <button type="button" onClick={() => setAll((v) => !v)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-brand-700">
                {all ? <><ChevronUp className="h-4 w-4" /> Show less</> : <><ChevronDown className="h-4 w-4" /> Show all {groups.length} sections</>}
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── 2 · The load calc ───────────────────────────────────────────────────── */

const NOT_NEEDED_REASONS = ["Same size as the old unit, checked", "A repair, not a replacement", "The builder sent the load calc", "Customer declined"];

function LoadCalcCard({ job, load, rules, closed }: { job: Job; load: EffectiveLoad; rules: LoadCalcRules; closed: boolean }) {
  const personOf = usePersonName();
  const save = useSaveLoadCalc();
  const sort = useSortFiles();
  const upload = useUploadFiles({ custno: job.custno, locno: job.locno ?? null, job_id: job.id, doc_tag: "load_calc", origin: "office", sort: true });
  const [mode, setMode] = useState<null | "type" | "skip">(null);
  const [heat, setHeat] = useState("");
  const [cool, setCool] = useState("");
  const [sqft, setSqft] = useState("");
  const [reason, setReason] = useState("");
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState<PegasusFile | null>(null);
  const pick = useRef<HTMLInputElement>(null);
  const fileNums = load.reports.map((f) => ({ f, l: loadRead(f) })).find((x) => x.l && (x.l.heat_loss_btuh || x.l.heat_gain_btuh));
  const unreadReports = load.reports.filter((f) => f.ai_at && !loadRead(f));
  const num = (s: string) => { const n = Number(s.replace(/[^\d.]/g, "")); return Number.isFinite(n) && n > 0 ? Math.round(n) : null; };

  const onFiles = (list: File[]) => {
    if (!list.length) return;
    upload.mutate(list, { onError: (e) => toastError(e instanceof Error ? `Upload failed: ${e.message}` : "Upload failed.") });
  };
  const onDrop = (e: DragEvent<HTMLElement>) => { e.preventDefault(); setDrag(false); onFiles([...(e.dataTransfer?.files ?? [])]); };
  const startType = () => {
    setHeat(String(load.heat ?? "")); setCool(String(load.cool ?? "")); setSqft(String(load.sqft ?? ""));
    setMode("type");
  };
  const busy = upload.isPending || load.reading;

  return (
    <Card id="zone-load-calc" className={cn("relative transition-colors", drag && "border-brand-400 ring-2 ring-brand-200")}>
      <div
        onDragOver={(e) => { if (!closed && e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDrag(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false); }}
        onDrop={onDrop}
      >
        <CardHead icon={Calculator} title="Load Calc"
          info={<>Do the Manual J in {rules.tool_label || "the load calc program"} from the tech&apos;s photos and facts — the satellite view helps with the outside walls. Drop the report here and the heating and cooling loads are read off it. Numbers typed here win over the report&apos;s.</>}
          right={rules.tool_url ? (
            <a href={rules.tool_url} target="_blank" rel="noreferrer" className={linkBtn}>
              Open {rules.tool_label || "the Program"} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
        />
        <div className="space-y-4 px-5 py-4">
          {load.source === "not_needed" ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-slate-500" />
              <span className="min-w-0 flex-1 text-[15px] text-slate-700">
                <b className="font-semibold text-slate-900">Not needed</b>{load.reason ? ` — ${load.reason}` : ""}
                <span className="text-slate-500"> · {personOf(load.by)}{load.at ? `, ${fmtDay(load.at)}` : ""}</span>
              </span>
              {!closed && <button type="button" onClick={() => save.mutate({ jobId: job.id, value: null })} className={linkBtn}><Undo2 className="h-4 w-4" /> Undo</button>}
            </div>
          ) : load.source !== "none" && mode !== "type" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <LoadTile icon={Flame} tone="heat" label="Heat Loss" value={load.heat} />
                <LoadTile icon={Snowflake} tone="cool" label="Heat Gain" value={load.cool} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] text-slate-500">
                {load.sqft ? <span><b className="font-semibold text-slate-700">{load.sqft.toLocaleString("en-US")}</b> sq ft</span> : null}
                {load.source === "file" && load.file ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-violet-500" />
                    Read off
                    <button type="button" onClick={() => setOpen(load.file!)} className="font-semibold text-brand-700 hover:underline">{load.file.filename}</button>
                    — check it against the report
                  </span>
                ) : (
                  <span>Typed by {personOf(load.by)}{load.at ? `, ${fmtDay(load.at)}` : ""}</span>
                )}
                {!closed && (
                  <span className="ml-auto flex items-center gap-1">
                    <button type="button" onClick={startType} className={linkBtn}><Pencil className="h-4 w-4" /> {load.source === "typed" ? "Edit" : "Type Instead"}</button>
                    {load.source === "typed" && fileNums && (
                      <button type="button" onClick={() => save.mutate({ jobId: job.id, value: null })} className={linkBtn}>Use the Report</button>
                    )}
                  </span>
                )}
              </div>
            </>
          ) : null}

          {mode === "type" && !closed && (
            <form onSubmit={(e) => {
              e.preventDefault();
              const h = num(heat), c = num(cool);
              if (!h && !c) return;
              save.mutate({ jobId: job.id, value: { heat_btu: h, cool_btu: c, sqft: num(sqft) } }, { onSuccess: () => setMode(null) });
            }} className="space-y-3 rounded-xl bg-slate-50 p-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <NumField label="Heat Loss" unit="Btu/h" value={heat} onChange={setHeat} autoFocus />
                <NumField label="Heat Gain" unit="Btu/h" value={cool} onChange={setCool} />
                <NumField label="Area" unit="sq ft" value={sqft} onChange={setSqft} />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setMode(null)}>Cancel</Button>
                <Button type="submit" size="sm" disabled={save.isPending || (!num(heat) && !num(cool))}><Check className="h-4 w-4" /> Save the Numbers</Button>
              </div>
            </form>
          )}

          {mode === "skip" && !closed && (
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!reason.trim()) return;
              save.mutate({ jobId: job.id, value: { not_needed: true, reason: reason.trim() } }, { onSuccess: () => setMode(null) });
            }} className="space-y-3 rounded-xl bg-slate-50 p-4">
              <div className="text-[14px] font-semibold text-slate-700">Why isn&apos;t one needed?</div>
              <div className="flex flex-wrap gap-1.5">
                {NOT_NEEDED_REASONS.map((r) => (
                  <button key={r} type="button" onClick={() => setReason(r)}
                    className={cn("h-9 rounded-full px-3.5 text-[14px] font-semibold transition-colors",
                      reason === r ? "bg-brand-600 text-white" : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-brand-50")}>{r}</button>
                ))}
              </div>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or say why…"
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25" />
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setMode(null)}>Cancel</Button>
                <Button type="submit" size="sm" disabled={save.isPending || !reason.trim()}>Mark Not Needed</Button>
              </div>
            </form>
          )}

          {load.source === "none" && mode === null && (
            <button type="button" disabled={closed || busy} onClick={() => pick.current?.click()}
              className="flex w-full flex-col items-center gap-1.5 rounded-xl border-2 border-dashed border-brand-200 bg-brand-50/40 px-4 py-7 text-center transition-colors hover:border-brand-400 hover:bg-brand-50 disabled:cursor-default disabled:opacity-70">
              {busy ? <Loader2 className="h-6 w-6 animate-spin text-brand-600" /> : <Upload className="h-6 w-6 text-brand-600" />}
              <span className="text-[15px] font-semibold text-slate-800">
                {upload.isPending ? "Uploading…" : load.reading ? "Reading the report…" : "Drop the load calc report here"}
              </span>
              {!busy && <span className="text-[13px] text-slate-500">or click to pick it — the heating and cooling loads fill in</span>}
            </button>
          )}
          <input ref={pick} type="file" accept=".pdf,image/*" multiple className="hidden" aria-label="Upload the load calc report"
            onChange={(e) => { const l = e.target.files ? [...e.target.files] : []; e.target.value = ""; onFiles(l); }} />

          {unreadReports.length > 0 && load.source === "none" && !load.reading && (
            <p className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[14px] text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              The numbers couldn&apos;t be read off {unreadReports[0].filename}.
              <button type="button" disabled={sort.isPending} onClick={() => sort.mutate({ ids: unreadReports.map((f) => f.id), force: true })} className="inline-flex items-center gap-1 font-semibold text-amber-900 underline underline-offset-2">
                <RefreshCw className="h-3.5 w-3.5" /> Read It Again
              </button>
              <span>or type them.</span>
            </p>
          )}

          {!closed && mode === null && load.source !== "not_needed" && (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
              {load.source === "none" && <button type="button" onClick={startType} className={linkBtn}><Pencil className="h-4 w-4" /> Type the Numbers</button>}
              {load.source !== "none" && <button type="button" onClick={() => pick.current?.click()} className={linkBtn}><Upload className="h-4 w-4" /> Add a Report</button>}
              <button type="button" onClick={() => { setReason(""); setMode("skip"); }} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700">
                Not Needed
              </button>
              {load.reports.length > 0 && (
                <span className="ml-auto text-[13px] text-slate-400">
                  {load.reports.length} report{load.reports.length === 1 ? "" : "s"} on file ·{" "}
                  <button type="button" onClick={() => setOpen(load.reports[0])} className="font-semibold text-brand-700 hover:underline">Open the latest</button>
                </span>
              )}
            </div>
          )}
        </div>
        {drag && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-brand-50/85 text-[15px] font-semibold text-brand-800">
            <Upload className="mr-2 h-5 w-5" /> Drop the report
          </div>
        )}
      </div>
      <FilePreviewModal file={open} custno={job.custno} onClose={() => setOpen(null)} />
    </Card>
  );
}

function LoadTile({ icon: Icon, label, value, tone }: { icon: typeof Flame; label: string; value: number | null; tone: "heat" | "cool" }) {
  return (
    <div className={cn("rounded-xl px-4 py-3 ring-1 ring-inset", tone === "heat" ? "bg-orange-50/60 ring-orange-100" : "bg-sky-50/60 ring-sky-100")}>
      <div className={cn("flex items-center gap-1.5 text-[13px] font-semibold", tone === "heat" ? "text-orange-700" : "text-sky-700")}>
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
        {value ? value.toLocaleString("en-US") : "—"}
        {value ? <span className="ml-1 text-sm font-semibold text-slate-500">Btu/h</span> : null}
      </div>
    </div>
  );
}

function NumField({ label, unit, value, onChange, autoFocus }: { label: string; unit: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[13px] font-semibold text-slate-600">{label}</span>
      <span className="relative block">
        <input value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d,]/g, ""))} inputMode="numeric" autoFocus={autoFocus}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-3 pr-14 text-right text-[15px] tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25" />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-slate-400">{unit}</span>
      </span>
    </label>
  );
}

/* ── 3 · The equipment check ─────────────────────────────────────────────── */

function EquipmentCheck({ job, units, accepted, specs, load, rules, st, closed, onDetails }: {
  job: Job; units: JobEquipment[]; accepted: QuoteOptionFull | null; specs: Map<string, ItemSpec> | undefined;
  load: EffectiveLoad; rules: LoadCalcRules; st: SoldState; closed: boolean; onDetails: () => void;
}) {
  const personOf = usePersonName();
  const confirm = useConfirmEquipment();
  const add = useAddJobEquipment();
  const { data: ahri } = useJobAhri(job.id);
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [swap, setSwap] = useState<string | null>(null);
  const majors = majorUnits(units);
  const addOns = units.filter((u) => u.kind === "accessory" || u.kind === "material");
  const texts = majors.map((u) => ({ ...unitText(u, specs), qty: u.qty, label: unitName(u) }));
  const lines = sizingCheck(texts, { heat: load.heat, cool: load.cool }, rules, (ahri ?? []).map((a) => ({ system_type: a.system_type, cooling_btuh: a.cooling_btuh, heating_btuh: a.heating_btuh })));
  // A coil is sold as an add-on — it still has to match the outdoor unit.
  const notes = matchNotes([...texts, ...addOns.map((u) => unitText(u, specs)).filter((t) => capacityOf(t).role === "coil")]);
  const off = lines.some((l) => l.verdict === "over" || l.verdict === "under");
  const soldLines = new Map((accepted?.lines ?? []).map((l) => [l.id, l]));
  const canConfirm = !closed && majors.length > 0 && st.loadOk;
  const record = () => ({
    load: { heat: load.heat, cool: load.cool, source: load.source },
    lines: lines.map((l) => ({ key: l.key, verdict: l.verdict, pct: l.pct != null ? Math.round(l.pct * 1000) / 1000 : null, capacity: l.capacity ?? null })),
    units: majors.map((u) => unitName(u)),
  });

  return (
    <Card id="zone-equip-check">
      <CardHead icon={BadgeCheck} title="Equipment Check"
        info={<>Each system is checked against the load: an air conditioner should be {rules.ac_min}–{rules.ac_max}% of the heat gain, a heat pump {rules.hp_min}–{rules.hp_max}%, and a furnace&apos;s output {rules.furnace_min}–{rules.furnace_max}% of the heat loss (ACCA Manual S). It uses the size in the model number — or the AHRI rating when the job has one — so it&apos;s a check, not the design; the load calc program&apos;s own equipment selection is the final word. The limits are in Settings → Sold Jobs &amp; Load Calc.</>}
        meta={st.done
          ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Confirmed</span>
          : st.stale ? <span className="font-semibold text-amber-700">Changed since it was confirmed</span>
          : <span>Not confirmed yet</span>}
      />
      <div className="space-y-4 px-5 py-4">
        {lines.length > 0 && (
          <div className={cn("grid gap-3", lines.length > 1 && "sm:grid-cols-2")}>
            {lines.map((l) => <SizingTile key={l.key} l={l} />)}
          </div>
        )}

        {majors.length === 0 ? (
          <p className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-4 text-[15px] text-amber-900">
            No equipment on the job yet — add it below.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-hairline">
            {majors.map((u) => (
              <UnitRow key={u.id} u={u} specs={specs} sold={u.quote_line_id ? soldLines.get(u.quote_line_id) ?? null : null}
                hasQuote={!!accepted} lines={lines} closed={closed} swapping={swap === u.id} onSwap={(v) => setSwap(v ? u.id : null)} />
            ))}
          </ul>
        )}

        {notes.length > 0 && (
          <ul className="space-y-1">
            {notes.map((n) => (
              <li key={n.text} className={cn("flex items-start gap-2 text-[14px]", n.ok ? "text-slate-600" : "font-medium text-amber-800")}>
                {n.ok ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                {n.text}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px]">
          {addOns.length > 0 && (
            <span className="min-w-0 text-slate-500">
              Add-ons: <span className="text-slate-700">{addOns.map((a) => unitName(a)).join(" · ")}</span>
            </span>
          )}
          {!closed && !adding && (
            <button type="button" onClick={() => setAdding(true)} className={linkBtn}><Plus className="h-4 w-4" /> Add a Unit</button>
          )}
          <button type="button" onClick={onDetails} className={cn(linkBtn, "ml-auto text-slate-500 hover:text-brand-700")}>
            Prices, Serials &amp; AHRI <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {adding && !closed && (
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-slate-600">Add a unit from the pricebook</span>
              <button type="button" onClick={() => setAdding(false)} className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600" aria-label="Close"><X className="h-4 w-4" /></button>
            </div>
            <PricebookPicker pickLabel="Add to Job" autoFocus browseMulti={false}
              onPick={(it) => add.mutate({ job_id: job.id, category: it.category ?? it.equip_type ?? null, manufacturer: it.manufacturer ?? null, model: it.model || it.part_number || "", part_number: it.part_number ?? null, unit_cost: itemCostOf(it), unit_price: null, item_id: it.id },
                { onSuccess: () => setAdding(false) })} />
          </div>
        )}

        {/* The sign-off */}
        {st.confirmed && !st.stale ? (
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3 ring-1 ring-inset ring-emerald-200">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
            <span className="min-w-0 flex-1 text-[15px] text-emerald-900">
              <b className="font-semibold">Confirmed</b> by {personOf(job.equip_confirmed_by)} · {fmtWhen(job.equip_confirmed_at)}
              {job.equip_confirm_note ? <span className="text-emerald-800"> — “{job.equip_confirm_note}”</span> : null}
            </span>
            {!closed && (
              <button type="button" disabled={confirm.isPending} onClick={() => confirm.mutate({ jobId: job.id, sig: null })}
                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">
                <Undo2 className="h-4 w-4" /> Undo
              </button>
            )}
          </div>
        ) : !closed && (
          <div className={cn("space-y-3 rounded-xl p-4 ring-1 ring-inset", st.stale ? "bg-amber-50/70 ring-amber-200" : "bg-slate-50 ring-hairline")}>
            {st.stale && (
              <p className="flex items-start gap-2 text-[14px] font-medium text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {personOf(job.equip_confirmed_by)} confirmed it {fmtDay(job.equip_confirmed_at)}, then the units changed — confirm what&apos;s on the job now.
              </p>
            )}
            <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={off ? "Why it stays this size — e.g. the customer wants the bigger unit" : "Note (optional) — e.g. load calc matches what was sold"}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25" />
            <div className="flex flex-wrap items-center justify-end gap-3">
              {!canConfirm && (
                <span className="mr-auto text-[14px] text-slate-500">
                  {majors.length === 0 ? "Add the equipment first." : "Record the load calc first — or mark it Not Needed."}
                </span>
              )}
              <Button disabled={!canConfirm || confirm.isPending || (off && !note.trim())}
                title={off && !note.trim() ? "Say why it stays this size" : undefined}
                onClick={() => confirm.mutate({ jobId: job.id, sig: st.sig, note, check: record() }, { onSuccess: () => setNote("") })}>
                {confirm.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {off ? "Confirm Anyway" : "Confirm the Equipment"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function SizingTile({ l }: { l: SizingLine }) {
  const pct = l.pct != null ? Math.round(l.pct * 100) : null;
  const tone = l.verdict === "right" ? "good" : l.verdict === "over" || l.verdict === "under" ? "warn" : "none";
  const word = l.verdict === "right" ? "Sized right" : l.verdict === "over" ? "Oversized" : l.verdict === "under" ? "Undersized"
    : l.verdict === "no_load" ? "Needs the load calc" : "Size not in the model";
  return (
    <div className={cn("rounded-xl px-4 py-3 ring-1 ring-inset",
      tone === "good" ? "bg-emerald-50/70 ring-emerald-200" : tone === "warn" ? "bg-amber-50/70 ring-amber-200" : "bg-slate-50 ring-hairline")}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[14px] font-semibold text-slate-700">
          {l.key === "heat" ? <Flame className="h-4 w-4 text-orange-500" /> : <Snowflake className="h-4 w-4 text-sky-500" />} {l.label}
        </span>
        <span className={cn("rounded-full px-2.5 py-0.5 text-[12.5px] font-bold",
          tone === "good" ? "bg-emerald-600 text-white" : tone === "warn" ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600")}>{word}</span>
      </div>
      {pct != null && <div className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900">{pct}%<span className="ml-1.5 text-sm font-semibold text-slate-500">of the load</span></div>}
      <div className="mt-1 text-[13px] leading-snug text-slate-600">
        {l.capacity != null ? <>{l.key === "heat" ? "≈" : ""}{btuh(l.capacity)}{l.key === "heat" ? " out" : ""}{l.capFrom === "ahri" ? " (AHRI)" : ""}</> : "Size unknown"}
        {l.load ? <> vs {btuh(l.load)} {l.key === "heat" ? "heat loss" : "heat gain"}</> : null}
      </div>
      {l.fits.length > 0 && l.verdict !== "right" && (
        <div className="mt-1.5 text-[13px] font-semibold text-slate-800">Fits the load: {l.fits.join(" · ")}</div>
      )}
    </div>
  );
}

function UnitRow({ u, specs, sold, hasQuote, lines, closed, swapping, onSwap }: {
  u: JobEquipment; specs: Map<string, ItemSpec> | undefined; sold: QuoteLine | null; hasQuote: boolean; lines: SizingLine[];
  closed: boolean; swapping: boolean; onSwap: (open: boolean) => void;
}) {
  const del = useDeleteJobEquipment();
  const [sure, setSure] = useState(false);
  const text = unitText(u, specs);
  const cap = capacityOf(text);
  const soldModel = sold ? (sold.model || sold.description || "").trim() : "";
  const nowModel = (u.model || u.part_number || "").trim();
  const changed = !!sold && !!soldModel && soldModel.toUpperCase() !== nowModel.toUpperCase();
  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-100">
          <RoleIcon role={cap.role} className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[15px] font-semibold text-slate-900">{(u.qty || 1) > 1 ? `${u.qty}× ` : ""}{unitName(u)}</span>
            {u.category && <span className="text-[13px] text-slate-500">{u.category}</span>}
          </div>
          <div className="mt-1"><Chips items={capacityChips(cap)} /></div>
          <div className="mt-1.5 text-[13px]">
            {changed ? (
              <span className="inline-flex items-center gap-1 font-semibold text-violet-700"><RefreshCw className="h-3.5 w-3.5" /> Changed — sold as {soldModel}</span>
            ) : sold ? (
              <span className="text-slate-500">As sold</span>
            ) : hasQuote ? (
              <span className="text-slate-500">Added after the sale</span>
            ) : null}
          </div>
        </div>
        {!closed && (
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => onSwap(!swapping)}
              className={cn("inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-colors",
                swapping ? "bg-brand-600 text-white" : "text-brand-700 hover:bg-brand-50")}>
              <RefreshCw className="h-4 w-4" /> Swap
            </button>
            {sure ? (
              <span className="inline-flex items-center gap-1 text-sm">
                <button type="button" onClick={() => del.mutate(u.id)} className="rounded-lg bg-rose-600 px-2 py-1 font-semibold text-white hover:bg-rose-700">Remove</button>
                <button type="button" onClick={() => setSure(false)} className="rounded-lg px-2 py-1 font-medium text-slate-500 hover:bg-slate-100">Keep</button>
              </span>
            ) : (
              <button type="button" onClick={() => setSure(true)} title="Remove from the job" aria-label="Remove from the job"
                className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        )}
      </div>
      {swapping && !closed && <SwapPanel u={u} cap={cap} lines={lines} onDone={() => onSwap(false)} />}
    </li>
  );
}

/** How alike two model numbers are, letter by letter (the size digits differ by design). */
function likeness(a: string, b: string): number {
  const x = a.toUpperCase(), y = b.toUpperCase();
  let n = 0;
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] === y[i]) n++;
  return n - Math.abs(x.length - y.length);
}

/** Other sizes of the same line (one click), or anything in the pricebook. The unit keeps its customer price
 *  and its link to the proposal line; our cost follows the new item. */
function SwapPanel({ u, cap, lines, onDone }: { u: JobEquipment; cap: Capacity; lines: SizingLine[]; onDone: () => void }) {
  const upd = useUpdateJobEquipment();
  const prefix = seriesPrefix(u.model || u.part_number, cap.role);
  const { data: series, isLoading } = useSeriesItems(prefix);
  const line = lines.find((l) => (cap.role === "furnace" || cap.role === "boiler" ? l.key === "heat" : l.key === "cool"));
  const sizes = useMemo(() => {
    const seen = new Set<string>();
    return (series ?? [])
      .filter((it) => it.id !== u.item_id && !it.inactive)
      .map((it) => ({ it, c: capacityOf({ category: it.category ?? it.equip_type, model: it.model, part_number: it.part_number, description: it.description, efficiency: it.efficiency }) }))
      .filter((x) => x.c.role === cap.role && (x.c.inputBtu != null || x.c.tons != null))
      .filter((x) => { const k = `${x.c.inputBtu ?? x.c.tons}|${(x.it.model || x.it.part_number || "").toUpperCase()}`; if (seen.has(k)) return false; seen.add(k); return true; })
      // Closest to the unit on the job first (same blower, same cabinet) — two per size is plenty.
      .map((x) => ({ ...x, near: likeness(u.model || u.part_number || "", x.it.model || x.it.part_number || "") }))
      .sort((a, b) => (a.c.inputBtu ?? (a.c.tons ?? 0) * 1000) - (b.c.inputBtu ?? (b.c.tons ?? 0) * 1000) || b.near - a.near)
      .filter((x, i, all) => all.slice(0, i).filter((y) => (y.c.inputBtu ?? y.c.tons) === (x.c.inputBtu ?? x.c.tons)).length < 2)
      .slice(0, 12);
  }, [series, u.item_id, cap.role]);
  const fits = (c: Capacity) => {
    if (!line?.range) return null;
    const v = cap.role === "furnace" || cap.role === "boiler" ? c.outputBtu : c.coolBtu;
    return v != null ? v >= line.range[0] && v <= line.range[1] : null;
  };
  const doSwap = (it: Pick<Item, "id" | "category" | "equip_type" | "manufacturer" | "model" | "part_number" | "average_cost" | "last_purchase">) =>
    upd.mutate({
      id: u.id, item_id: it.id, category: it.category ?? it.equip_type ?? u.category, manufacturer: it.manufacturer ?? u.manufacturer,
      model: it.model || it.part_number || u.model, part_number: it.part_number ?? null, unit_cost: itemCostOf(it),
    }, { onSuccess: onDone });
  return (
    <div className="ml-12 mt-3 space-y-3 rounded-xl bg-slate-50 p-3">
      {prefix && (
        <div>
          <div className="mb-1.5 text-[13px] font-semibold text-slate-600">Other sizes of {prefix.replace(/-$/, "")}</div>
          {isLoading ? <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking in the pricebook…</span>
            : sizes.length === 0 ? <span className="text-[13px] text-slate-400">No other sizes of this line in the pricebook.</span>
            : (
              <div className="flex flex-wrap gap-1.5">
                {sizes.map(({ it, c }) => {
                  const f = fits(c);
                  const size = c.inputBtu != null ? `${Math.round(c.inputBtu / 1000)}K` : `${fmtTons(c.tons ?? 0)} Ton`;
                  return (
                    <button key={it.id} type="button" disabled={upd.isPending} onClick={() => doSwap(it as unknown as Item)}
                      title={it.description ?? undefined}
                      className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ring-1 ring-inset transition-colors disabled:opacity-50",
                        f ? "bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100" : "bg-white text-slate-700 ring-slate-200 hover:bg-brand-50")}>
                      <span className="tabular-nums">{size}</span>
                      <span className="font-mono text-[12px] font-medium text-slate-500">{it.model || it.part_number}</span>
                      {f && <span className="rounded-full bg-emerald-600 px-1.5 text-[11px] font-bold text-white">Fits</span>}
                    </button>
                  );
                })}
              </div>
            )}
        </div>
      )}
      <div>
        <div className="mb-1.5 text-[13px] font-semibold text-slate-600">Or pick anything in the pricebook</div>
        <PricebookPicker pickLabel="Swap to This" browseMulti={false} onPick={(it) => doSwap(it)} />
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={onDone} className="text-sm font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

